import { Box, Grid } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as ReloadIcon } from '../../assets/reload.svg';
import NumberWidget from 'components/Widgets/NumberWidget';
import moment from 'moment';
import {
	statusOverviewData,
	failureReasonData,
	expensiveFailureData,
	expensiveFailureCostData,
	peakHourData,
	activeWorkspaceData,
	hourByJobCountData,
	topWorkspacesData,
	workspaceGetEnv,
	jobLastSyncData,
	expensiveSuccessData,
	fetchSectorList
} from 'service/service';
import 'components/DatabricksADB/ADBStyles.scss';
import { useTheme } from "@mui/material/styles";
import { CustomizedDataGrid } from 'components/customized_data_grid';
import PageWrapper from 'components/Common/PageWraper';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import PieChart from 'components/Widgets/PieChart';
import MultiLineChartWidget from 'components/Widgets/MultiLineChartWidget';
import { useLocation, useNavigate } from 'react-router-dom';
import FiltersBar from './FilterBar';
import { toTitleCase } from './workspace';
import { useAdbBaseState } from 'utils/adb-common-util';
interface Row {
	storageAccount: string;
	resourceGroup: string;
	size: string;
	monthlyCost: string;
	growth: string;
	owner: string;
}

const JobCell = ({ row, onClick }: { row: any; onClick: (row: any) => void }) => (
	<Box
		sx={{
			cursor: 'pointer',
			color: '#4fc3f7',
			'&:hover': {
				textDecoration: 'underline',
				color: '#80d4ff'
			}
		}}
		onClick={() => onClick(row)}
		title={row?.job_name}
	>
		{row?.job_name || '--'}
	</Box>
);

const WorkspaceCell = ({ row, onClick }: { row: any; onClick: (row: any) => void }) => (
	<Box
		onClick={() => onClick(row)}
		title={row?.workspace_name}
	>
		{row?.workspace_name || '--'}
	</Box>
);

const CostCell = ({ row, columnKey }: { row: any; columnKey: string }) => (
	<div>{row[columnKey] || '--'}</div>
);

const TimestampCell = ({ row, columnKey }: { row: any; columnKey: string }) => {
	const timestamp = row[columnKey];
	const formatted = timestamp
		? moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
		: '--';
	return <div>{formatted}</div>;
};

const JobFormatter = ({ row, column }: { row: any; column: any }) => (
	<JobCell row={row} onClick={column.handleCellClick} />
);

const WorkspaceFormatter = ({ row, column }: { row: any; column: any }) => (
	<WorkspaceCell row={row} onClick={column.handleCellClick} />
);

const CostFormatter = ({ row, column }: { row: any; column: any }) => (
	<CostCell row={row} columnKey={column.key} />
);

const TimestampFormatter = ({ row, column }: { row: any; column: any }) => (
	<TimestampCell row={row} columnKey={column.key} />
);

const RuntimeFormatter = ({ row, column }: { row: any; column: any }) => (
	<div>{row[column.key] || '--'}</div>
);

const columnMapping: Record<string, string> = {
	job_name: 'Job Name',
	workspace_name: 'Workspace Name',
	dbu_consumption: 'DBU Consumption',
	cost: 'Cost',
	failure_count: 'Failure Count',
	failure_timestamp: 'Last Failure Time',
	avg_runtime_minutes: 'Avg.Run Time'
};

const succeColumnMapping: Record<string, string> = {
	job_name: 'Job Name',
	workspace_name: 'Workspace Name',
	dbu_consumption: 'DBU Consumption',
	cost: 'Cost',
	success_count: 'Success Count',
	success_timestamp: 'Last Success Time',
	avg_runtime_minutes: 'Avg.Run Time'
};

const formatRuntime = (minutes: number) => {
	let remainingSeconds = minutes * 60;
	const days = Math.floor(remainingSeconds / (24 * 60 * 60));
	remainingSeconds %= 24 * 60 * 60;
	const hours = Math.floor(remainingSeconds / (60 * 60));
	remainingSeconds %= 60 * 60;
	const mins = Math.floor(remainingSeconds / 60);
	const secs = Math.round(remainingSeconds % 60);

	const parts = [];
	if (days) parts.push(`${days} day${days > 1 ? 's' : ''}`);
	if (hours) parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
	if (mins) parts.push(`${mins} min${mins > 1 ? 's' : ''}`);
	if (secs || parts.length === 0) parts.push(`${secs} sec${secs === 1 ? '' : 's'}`);

	return parts.join(' ');
};

const filterTableData = (data: any[]) => {
	return data.map((row) => {
		const filtered: Record<string, any> = {};
		Object.keys(columnMapping).forEach((key) => {
			if (key === 'dbu_consumption' && typeof row[key] === 'number') {
				filtered[key] = Number(row[key].toFixed(2));
			} else if (key === 'cost' && typeof row[key] === 'number') {
				filtered[key] = `$${row[key].toFixed(2)}`;
			} else if (key === 'avg_runtime_minutes' && typeof row[key] === 'number') {
				filtered[key] = formatRuntime(row[key]);
			} else {
				filtered[key] = row[key];
			}
		});
		return filtered;
	});
};

const filterSucceTableData = (data: any[]) => {
	return data.map((row) => {
		const filtered: Record<string, any> = {};
		Object.keys(succeColumnMapping).forEach((key) => {
			if (key === 'dbu_consumption' && typeof row[key] === 'number') {
				filtered[key] = Number(row[key].toFixed(2));
			} else if (key === 'cost' && typeof row[key] === 'number') {
				filtered[key] = `$${row[key].toFixed(2)}`;
			} else if (key === 'avg_runtime_minutes' && typeof row[key] === 'number') {
				filtered[key] = formatRuntime(row[key]);
			} else {
				filtered[key] = row[key];
			}
		});
		return filtered;
	});
};

const defaultColumns = ['Job Name', 'Workspace Name', 'DBU Consumption', 'Cost', 'Failure Count', 'Last Failure Time', 'Avg.Run Time'];
const defaultSucceColumns = ['Job Name', 'Workspace Name', 'DBU Consumption', 'Cost', 'Success Count', 'Last Success Time', 'Avg.Run Time'];

interface FetchParams {
	startDate: string;
	endDate: string;
	env: string;
	sector: string;
	location: string;
	selectedDateInString: string;
	page: number;
	pageSize: number;
	sortingInfo?: { sortBy: string; sortDir: string };
}

const JobsADB = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const routerLocation = useLocation();
	const navigationState = routerLocation.state as {
		startDate?: string;
		endDate?: string;
		env?: string;
		sector?: string;
		location?: string;
		selectedDateInString?: string;
		activeTab?: number;
	} | null;

	const {
		restoredRef,
		prevParamsRef,
		PRESERVE_FILTER_KEY,
		yesterday,
		defStartDate,
		...defaults
	} = useAdbBaseState(navigationState, 'job_filters');
	const DEFAULT_FILTERS = defaults;

	const [location, setLocation] = useState(DEFAULT_FILTERS.location);
	const tabParam = new URLSearchParams(routerLocation.search).get('tab');
	let activeTab = navigationState?.activeTab ?? 0;

	if (tabParam === '1') {
		activeTab = 1;
	} else if (tabParam === '0') {
		activeTab = 0;
	}
	const [sectorName, setSectorName] = useState('all');
	const [sector, setSector] = useState<string>(DEFAULT_FILTERS.sector);
	const [env, setEnv] = useState(DEFAULT_FILTERS.env);
	const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
	const [startDate, setStartDate] = useState<string>(DEFAULT_FILTERS.startDate);
	const [endDate, setEndDate] = useState<string>(DEFAULT_FILTERS.endDate);
	const [selectedDateInString, setSelectedDateInString] = useState<string>(DEFAULT_FILTERS.selectedDateInString);

	const handleDateSelect = (stDt: string, edDt: string, selectedDate: string = 'Hrs') => {
		setSelectedDateInString(selectedDate);
		const yesterday = moment.tz('Asia/Kolkata').tz('Asia/Kolkata').subtract(1, 'day');
		const end = yesterday.endOf('day');
		let start;

		if (selectedDate === 'Hrs') {
			start = yesterday.startOf('day');
		} else if (selectedDate === '7d') {
			start = yesterday.clone().subtract(6, 'days').startOf('day');
		} else if (selectedDate === '30d') {
			start = yesterday.clone().subtract(29, 'days').startOf('day');
		} else if (selectedDate === 'custom') {
			start = moment.tz(stDt, 'Asia/Kolkata').startOf('day');
		} else {
			start = moment.tz(stDt, 'Asia/Kolkata').startOf('day');
		}
		setStartDate(start.format('YYYY-MM-DD'));
		setEndDate(end.format('YYYY-MM-DD'));
	};
	const handleFailedJobClick = useCallback((row: any) => {
		const workspaceId = row.workspace_id || row.workspace_name;
		const workspaceName = row.workspace_name;
		const jobName = row.job_name;

		navigate('/databricksADB/failed-jobs-cost-details', {
			state: {
				dateLabel: `Top 100 Most Expensive Failed Jobs - ${workspaceName || workspaceId}`,
				workspaceId: workspaceId,
				workspaceName: workspaceName,
				jobName: jobName,
				resultState: 'FAILED',
				jobType: 'failed',
				startDate,
				endDate,
				env,
				sector: sectorName,
				location,
				selectedDateInString,
				timestamp: Date.now()
			},
			replace: true
		});
	}, [navigate, startDate, endDate, env, sectorName, location, selectedDateInString]);

	const handleSuccessJobClick = useCallback((row: any) => {
		const workspaceId = row.workspace_id || row.workspace_name;
		const workspaceName = row.workspace_name;
		const jobName = row.job_name;

		navigate('/databricksADB/failed-jobs-cost-details', {
			state: {
				dateLabel: `Top 100 Most Expensive Succeeded Jobs - ${workspaceName || workspaceId}`,
				workspaceId,
				workspaceName,
				jobName: jobName,
				resultState: 'SUCCEEDED',
				jobType: 'success',
				startDate,
				endDate,
				env,
				sector: sectorName,
				location,
				selectedDateInString,
				timestamp: Date.now()
			},
			replace: true
		});
	}, [navigate, startDate, endDate, env, sectorName, location, selectedDateInString]);

	const columns = useMemo(() => {
		return Object.entries(columnMapping).map(([key, displayName]) => {
			const base = { key, name: displayName, width: 'auto' };
			if (key === 'job_name') return { ...base, formatter: JobFormatter, handleCellClick: handleFailedJobClick };
			if (key === 'workspace_name') return { ...base, formatter: WorkspaceFormatter, handleCellClick: handleFailedJobClick };
			if (key === 'cost') return { ...base, formatter: CostFormatter };
			if (key === 'failure_timestamp') return { ...base, formatter: TimestampFormatter };
			if (key === 'avg_runtime_minutes') return { ...base, formatter: RuntimeFormatter };
			return base;
		});
	}, [handleFailedJobClick]);

	const columnsSucce = useMemo(() => {
		return Object.entries(succeColumnMapping).map(([key, displayName]) => {
			const base = { key, name: displayName, width: 'auto' };
			if (key === 'job_name') return { ...base, formatter: JobFormatter, handleCellClick: handleSuccessJobClick };
			if (key === 'workspace_name') return { ...base, formatter: WorkspaceFormatter, handleCellClick: handleSuccessJobClick };
			if (key === 'cost') return { ...base, formatter: CostFormatter };
			if (key === 'success_timestamp') return { ...base, formatter: TimestampFormatter };
			if (key === 'avg_runtime_minutes') return { ...base, formatter: RuntimeFormatter };
			return base;
		});
	}, [handleSuccessJobClick]);
	const pageSizeOptions = [10, 20, 30, 40, 50];
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [successPage, setSuccessPage] = useState(1);
	const [successPageSize, setSuccessPageSize] = useState(10);
	const [successPaginationData, setSuccessPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [sortingInfo, setSortingInfo] = useState({
		sortBy: "",
		sortDir: "",
	});
	const [loader, setLoader] = useState({
		statusOverviewType: false,
		failureReasonType: false,
		failureCostType: false,
		PeakHourType: false,
		activeWorkspaceType: false,
		hourByJobCountType: false,
		topWorkspacesType: false,
		env: false,
		expensiveFailureType: false,
		expensiveSuccessType: false
	});
	const [jobstatusOverview, setJobstatusOverview] = useState<{ _id: string; totalJobCount: number }[]>([]);
	const [successRate, setSuccessRate] = useState<string>('0.00%');
	const [jobfailureReason, setJobfailureReason] = useState<{ _id: string; totalFailures: number }[]>([]);
	const [jobfailureCost, setJobfailureCost] = useState<{ _id: null; totalCost: number }[]>([]);
	const [jobPeakHour, setJobPeakHour] = useState<{ total_job_runs: number; peak_hour: number }[]>([]);
	const [activeWorkspace, setActiveWorkspace] = useState<{ total_job_runs: number; active_workspace_id: string; active_workspace_name: string }[]>([]);
	const [hourByJobCount, setHourByJobCount] = useState<{ total_job_runs: number; peak_hour: number }[]>([]);
	const [peakHourXAxisLabel, setPeakHourXAxisLabel] = useState<string[]>([]);
	const [expensiveFailure, setExpensiveFailure] = useState<any[]>([]);
	const [allExpensiveFailures, setAllExpensiveFailures] = useState<any[]>([]);
	const [expensiveSuccess, setExpensiveSuccess] = useState<any[]>([]);
	const [allExpensiveSuccesses, setAllExpensiveSuccesses] = useState<any[]>([]);
	const [lastSync, setLastSync] = useState(null);
	const [sectorList, setSectorList] = useState<string[]>([]);

	const fetchSectors = async () => {
		const resp = await fetchSectorList({});
		if (resp?.success) {
			setSectorList(resp?.data || []);
		}
	};

	useEffect(() => {
		fetchSectors()
	}, [])
	interface SeriesItem {
		name: string;
		type: 'line';
		smooth: boolean;
		data: number[];
	}
	const [peakHourSeriesData, setPeakHourSeriesData] = useState<SeriesItem[]>([]);

	const handleSorting = (sortingInfo: any) => {
		setSortingInfo(sortingInfo);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		if (value === page) return;

		setPage(value);
		const startIndex = (value - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		setExpensiveFailure(allExpensiveFailures.slice(startIndex, endIndex));
	};

	const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newSize = Number(event.target.value);

		setPage(1);
		setPageSize(newSize);
		const startIndex = 0;
		const endIndex = newSize;
		setExpensiveFailure(allExpensiveFailures.slice(startIndex, endIndex));
		setPaginationData((prev) => ({
			...prev,
			totalPages: Math.ceil(allExpensiveFailures.length / newSize)
		}));
	};

	const handleSuccessPageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		if (value === successPage) return;
		setSuccessPage(value);
		const startIndex = (value - 1) * successPageSize;
		const endIndex = startIndex + successPageSize;
		setExpensiveSuccess(allExpensiveSuccesses.slice(startIndex, endIndex));
	};

	const handleSuccessPageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newSize = Number(event.target.value);
		setSuccessPage(1);
		setSuccessPageSize(newSize);
		const startIndex = 0;
		const endIndex = newSize;
		setExpensiveSuccess(allExpensiveSuccesses.slice(startIndex, endIndex));
		setSuccessPaginationData((prev) => ({
			...prev,
			totalPages: Math.ceil(allExpensiveSuccesses.length / newSize)
		}));
	};

	const compactFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
		notation: 'compact',
		compactDisplay: 'short',
		maximumFractionDigits: 2
	}), []);

	const fetchLastSync = async () => {
		const res = await jobLastSyncData();
		if (res?.success && res?.statusCode === 200) {
			const date = res.data?.End_Time;
			setLastSync(date);
		}
	};

	const formattedLastSync = lastSync
		? (() => {
			const date = new Date(lastSync);
			const weekday = date.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' });
			const day = date.toLocaleString('en-US', { day: '2-digit', timeZone: 'UTC' });
			const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
			const year = date.toLocaleString('en-US', { year: 'numeric', timeZone: 'UTC' });
			return `${weekday}, ${day} ${month} ${year}(UTC)`;
		})()
		: 'Loading...';


	const statusOverviewFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			statusOverviewType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await statusOverviewData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			const data = resp.data || [];
			setJobstatusOverview(data);
			const totalJobs = data.filter((item: { _id: string; }) => item._id !== "SKIPPED").reduce((sum: any, item: { totalJobCount: any; }) => sum + item.totalJobCount, 0);
			const succeededItem = data.find((item: { _id: string }) => item._id === 'SUCCEEDED');
			const succeededCount = succeededItem?.totalJobCount || 0;
			const ratio = totalJobs > 0 ? (succeededCount / totalJobs) * 100 : 0;
			setSuccessRate(`${ratio.toFixed(2)}%`);
		}
		setLoader((prev) => ({
			...prev,
			statusOverviewType: false
		}));
	};

	const failureReasonFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			failureReasonType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await failureReasonData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			setJobfailureReason(resp?.data);
		}
		setLoader((prev) => ({
			...prev,
			failureReasonType: false
		}));
	};

	const expensiveFailureCostFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			failureCostType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await expensiveFailureCostData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			setJobfailureCost(resp?.data);
		}
		setLoader((prev) => ({
			...prev,
			failureCostType: false
		}));
	};

	const peakHourFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			PeakHourType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await peakHourData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			setJobPeakHour(resp?.data);
		}
		setLoader((prev) => ({
			...prev,
			PeakHourType: false
		}));
	};

	const activeWorkspaceFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			activeWorkspaceType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await activeWorkspaceData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			setActiveWorkspace(resp?.data);
		}
		setLoader((prev) => ({
			...prev,
			activeWorkspaceType: false
		}));
	};

	const hourByJobCountFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			hourByJobCountType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await hourByJobCountData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			setHourByJobCount(resp?.data);
		}
		setLoader((prev) => ({
			...prev,
			hourByJobCountType: false
		}));
	};

	const topWorkspacesFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
		setLoader((prev) => ({
			...prev,
			topWorkspacesType: true
		}));
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await topWorkspacesData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
		if (resp?.success) {
			const hours = resp.data[0]?.hours?.map((h: any) => `${h.hour_of_day}:00`) || [];
			setPeakHourXAxisLabel(hours);

			const multiSeries: SeriesItem[] = resp.data.map((workspace: any) => ({
				name: workspace.workspace_name,
				type: 'line',
				smooth: true,
				data: workspace.hours.map((h: any) => h.totalJobRunCount)
			}));
			setPeakHourSeriesData(multiSeries);
		}

		setLoader((prev) => ({
			...prev,
			topWorkspacesType: false
		}));
	};

	const expensiveFailureFetch = async (params: FetchParams) => {
		const { startDate, endDate, env, sector, location, selectedDateInString, page: p, pageSize: s, sortingInfo } = params;
		setLoader((prev) => ({ ...prev, expensiveFailureType: true }));

		const { sortBy, sortDir } = sortingInfo ?? { sortBy: '', sortDir: '' };
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await expensiveFailureData({
			startDate: startFormatted,
			endDate: endFormatted,
			env,
			sector,
			location,
			selectedDateInString,
			page: 1,
			limit: 100,
			sortField: sortBy,
			sortOrder: sortDir
		});

		if (resp?.success) {
			const docs = resp.data?.docs ?? [];
			const filteredDocs = filterTableData(docs);
			setAllExpensiveFailures(filteredDocs);

			const startIndex = (p - 1) * s;
			const endIndex = startIndex + s;
			setExpensiveFailure(filteredDocs.slice(startIndex, endIndex));

			setPage(p);
			setPageSize(s);
			setPaginationData({
				totalElements: filteredDocs.length,
				totalPages: Math.ceil(filteredDocs.length / s)
			});
		}

		setLoader((prev) => ({ ...prev, expensiveFailureType: false }));
	};

	const expensiveSuccessFetch = async (params: FetchParams) => {
		const { startDate, endDate, env, sector, location, selectedDateInString, page: successPage, pageSize: successPageSize, sortingInfo } = params;
		setLoader((prev) => ({ ...prev, expensiveSuccessType: true }));

		const { sortBy, sortDir } = sortingInfo ?? { sortBy: '', sortDir: '' };
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		const resp = await expensiveSuccessData({
			startDate: startFormatted,
			endDate: endFormatted,
			env,
			sector,
			location,
			selectedDateInString,
			page: 1,
			limit: 100,
			sortField: sortBy,
			sortOrder: sortDir
		});

		if (resp?.success) {
			const docs = resp.data?.docs ?? [];
			const filteredDocs = filterSucceTableData(docs);
			setAllExpensiveSuccesses(filteredDocs);

			const startIndex = (successPage - 1) * successPageSize;
			const endIndex = startIndex + successPageSize;
			setExpensiveSuccess(filteredDocs.slice(startIndex, endIndex));

			setSuccessPage(successPage);
			setSuccessPageSize(successPageSize);
			setSuccessPaginationData({
				totalElements: filteredDocs.length,
				totalPages: Math.ceil(filteredDocs.length / successPageSize)
			});
		}

		setLoader((prev) => ({ ...prev, expensiveSuccessType: false }));
	};
	const colorMap: Record<string, string> = { SUCCEEDED: '#01C008', SKIPPED: '#FFA500', FAILED: '#FE2626' };
	const failedStatuses = new Set(['ERROR', 'CANCELLED', 'TIMED_OUT', 'FAILED']);
	const pieChartData = useMemo(() => {
		const groupedData: { value: number; name: string; itemStyle?: { color: string } }[] = [];
		let failedTotal = 0;

		jobstatusOverview.forEach((item) => {
			if (failedStatuses.has(item._id)) {
				failedTotal += item.totalJobCount;
			} else {
				const name = item._id.replaceAll('_', ' ').toUpperCase();
				groupedData.push({
					value: item.totalJobCount,
					name,
					itemStyle: { color: colorMap[item._id] ?? '#999' }
				});
			}
		});

		if (failedTotal > 0) {
			groupedData.push({
				value: failedTotal,
				name: 'FAILED',
				itemStyle: { color: colorMap['FAILED'] }
			});
		}

		return groupedData;
	}, [jobstatusOverview]);

	const failureReasonChartData = useMemo(() => {
		return jobfailureReason.map((item) => ({
			value: item.totalFailures,
			name: item._id.replaceAll('_', ' ')
		}));
	}, [jobfailureReason]);

	const FAILURE_REASON_COLORS = ['#299BFF', '#1C67F6', '#00D08D', '#01C008', '#FFA200', '#F4793B', '#FE2626', '#FF00D6', '#B8107F', '#9D4200'];

	const commonPeakHoursChartData = useMemo(() => {
		return hourByJobCount.map((item) => ({
			name: `Hour ${item.peak_hour}`,
			totalJobRuns: item.total_job_runs
		}));
	}, [hourByJobCount]);

	const commonPeakHoursSeries = useMemo(() => [
		{
			key: 'totalJobRuns',
			name: 'Job Runs',
			color: '#FD2626',
			smooth: false,
			symbolSize: 6
		}
	], []);

	const overallPeakHoursChartData = useMemo(() => {
		return peakHourXAxisLabel.map((hour, hourIndex) => {
			const row: Record<string, string | number> = {
				name: hour
			};

			peakHourSeriesData.forEach((workspace, seriesIndex) => {
				row[`workspace_${seriesIndex}`] = workspace.data[hourIndex] ?? 0;
			});

			return row;
		});
	}, [peakHourXAxisLabel, peakHourSeriesData]);

	const overallPeakHoursSeries = useMemo(() => {
		const colors = [
			'#01BF08',
			'#eabd2b',
			'#A8ACCA',
			'#ffa200',
			'#FD2626'
		];
		return peakHourSeriesData.map((workspace, index) => ({
			key: `workspace_${index}`,
			name: workspace.name,
			color: colors[index % colors.length],
			smooth: false,
			symbolSize: 6
		}));
	}, [peakHourSeriesData]);

	useEffect(() => {
		if (!(startDate && endDate && env && sector && location && selectedDateInString)) return;

		let mounted = true;
		const runAll = async () => {
			try {
				await Promise.all([
					statusOverviewFetch(startDate, endDate, env, sector, location, selectedDateInString),
					failureReasonFetch(startDate, endDate, env, sector, location, selectedDateInString),
					expensiveFailureCostFetch(startDate, endDate, env, sector, location, selectedDateInString),
					peakHourFetch(startDate, endDate, env, sector, location, selectedDateInString),
					activeWorkspaceFetch(startDate, endDate, env, sector, location, selectedDateInString),
					hourByJobCountFetch(startDate, endDate, env, sector, location, selectedDateInString),
					topWorkspacesFetch(startDate, endDate, env, sector, location, selectedDateInString),
					fetchLastSync()
				]);
			} catch (err) {
				console.error('Workspace fetches failed', err);
			} finally {
				if (mounted && restoredRef.current) {
					sessionStorage.removeItem(PRESERVE_FILTER_KEY);
					restoredRef.current = false;
				}
			}
		};

		runAll();
		return () => { mounted = false; };
	}, [startDate, endDate, env, sector, location, selectedDateInString]);

	useEffect(() => {
		expensiveFailureFetch({ startDate, endDate, env, sector, location, selectedDateInString, page, pageSize, sortingInfo });
	}, [startDate, endDate, env, sector, location, selectedDateInString, page, pageSize, sortingInfo]);

	useEffect(() => {
		expensiveSuccessFetch({ startDate, endDate, env, sector, location, selectedDateInString, page: successPage, pageSize: successPageSize, sortingInfo });
	}, [startDate, endDate, env, sector, location, selectedDateInString, successPage, successPageSize, sortingInfo]);

	useEffect(() => {
		getEnvFetch(location);
	}, [location]);

	const restoreDateFilters = useCallback((navState: typeof navigationState, preservedRaw: string | null) => {
		if (navState?.selectedDateInString?.trim() !== 'custom') {
			if (navState?.startDate) setStartDate(navState.startDate);
			if (navState?.endDate) setEndDate(navState.endDate);
			if (navState?.selectedDateInString) setSelectedDateInString(navState.selectedDateInString);
		}

		if (preservedRaw && navState?.selectedDateInString === 'customDateRange') {
			const p = JSON.parse(preservedRaw);
			if (p.startDate) setStartDate(p.startDate);
			if (p.endDate) setEndDate(p.endDate);
			if (p.selectedDateInString) setSelectedDateInString(p.selectedDateInString);
			restoredRef.current = true;
		}
	}, []);

	const restoreBaseFilters = useCallback((navState: typeof navigationState) => {
		if (navState?.env) setEnv(navState.env);
		if (navState?.sector) {
			setSector(navState.sector);
			setSectorName(navState.sector);
		}
		if (navState?.location) setLocation(navState.location);
	}, []);

	const restoreFiltersFromNavigation = useCallback(() => {
		if (!navigationState) return;

		const preservedRaw = sessionStorage.getItem(PRESERVE_FILTER_KEY);

		restoreDateFilters(navigationState, preservedRaw);
		restoreBaseFilters(navigationState);

		initialEnvAppliedRef.current = false;
	}, [navigationState, restoreDateFilters, restoreBaseFilters]);

	useEffect(() => {
		restoreFiltersFromNavigation();
	}, [restoreFiltersFromNavigation]);

	const initialEnvAppliedRef = useRef(false);
	const envRef = useRef(env);
	useEffect(() => { envRef.current = env; }, [env]);
	const getEnvFetch = async (location: string) => {
		try {
			setLoader((prev) => ({
				...prev,
				env: true
			}));
			const res = await workspaceGetEnv(location);
			const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
			setEnvOptions(options);
			if (!initialEnvAppliedRef.current && navigationState?.env) {
				setEnv(navigationState.env);
				initialEnvAppliedRef.current = true;
			} else {
				const availableValues = options.map((o) => o.value);
				const current = envRef.current;
				if (!availableValues.includes(current)) {
					const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
					setEnv(fallback);
				}
			}
			setLoader((prev) => ({
				...prev,
				env: false
			}));
		} catch (error) {
			console.error('Failed to fetch environment options:', error);
		}
	};
	const updateHistoryState = (overrides: Partial<{
		startDate: string;
		endDate: string;
		env: string;
		sector: string;
		location: string;
		selectedDateInString: string;

	}>) => {
		const newState = {
			startDate,
			endDate,
			env,
			sector: sectorName,
			location,
			selectedDateInString,
			...overrides
		};
		navigate(routerLocation.pathname, { state: newState, replace: true });
	};

	const handleStatusOverviewClick = (params: any) => {
		if (params?.name) {
			const snap = {
				startDate,
				endDate,
				selectedDateInString,
			};
			sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
			navigate('/databricksADB/statusOverview-cost', {
				state: {
					computeType: params.name,
					startDate,
					endDate,
					env,
					sector: sectorName,
					location,
					selectedDateInString
				}
			});
		}
	};
	const handleFailureReasonClick = (params: any) => {
		if (params?.name) {
			const snap = {
				startDate,
				endDate,
				selectedDateInString,
			};
			sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
			navigate('/databricksADB/FailureReasons', {
				state: {
					computeType: params.name,
					startDate,
					endDate,
					env,
					sector: sectorName,
					location,
					selectedDateInString,
					failureReason: params.name
				}
			});
		}
	};

	const handlePeakHourClick = useCallback(() => {
		const peakHour = jobPeakHour[0]?.peak_hour;

		const snap = {
			startDate,
			endDate,
			selectedDateInString,
		};
		sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
		navigate('/databricksADB/peakHourDetails', {
			state: {
				dateLabel: `Overall Peak Hour - Hour ${peakHour}:00`,
				filterType: 'peak_hour',
				peakHour: peakHour,
				startDate,
				endDate,
				env,
				sector: sectorName,
				location,
				selectedDateInString
			}
		});
	}, [navigate, jobPeakHour, startDate, endDate, env, sectorName, location, selectedDateInString]);

	const handleActiveWorkspaceClick = useCallback(() => {
		const workspaceId = activeWorkspace[0]?.active_workspace_id;
		const workspaceName = activeWorkspace[0]?.active_workspace_name;

		const snap = {
			startDate,
			endDate,
			selectedDateInString,
		};
		sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
		navigate('/databricksADB/peak-hour-details', {
			state: {
				dateLabel: `Peak Hour Analysis - ${workspaceName}`,
				filterType: 'workspace',
				workspaceId: workspaceId,
				workspaceName: workspaceName,
				startDate,
				endDate,
				env,
				sector: sectorName,
				location,
				selectedDateInString,
				fromActiveWorkspaceKPI: true
			}
		});
	}, [navigate, activeWorkspace, startDate, endDate, env, sectorName, location, selectedDateInString]);

	const handleCommonPeakHoursClick = useCallback((params: any) => {
		if (!params?.name) return;
		const hourMatch = params.name.match(/Hour (\d+)/);
		const hour = hourMatch ? Number.parseInt(hourMatch[1]) : null;

		const snap = {
			startDate,
			endDate,
			selectedDateInString,
		};
		sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
		navigate('/databricksADB/peakHourDetails', {
			state: {
				dateLabel: `Common Peak Hours - Hour ${hour}:00`,
				filterType: 'peak_hour',
				peakHour: hour,
				startDate,
				endDate,
				env,
				sector: sectorName,
				location,
				selectedDateInString
			}
		});
	}, [navigate, startDate, endDate, env, sectorName, location, selectedDateInString]);

	const handleOverallPeakHoursClick = useCallback((params: any) => {
		if (!params?.seriesName) return;
		const workspaceName = params.seriesName;

		const snap = {
			startDate,
			endDate,
			selectedDateInString,
		};
		sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
		navigate('/databricksADB/peakHourDetails', {
			state: {
				dateLabel: `Overall Peak Hours by Workspace - ${workspaceName}`,
				filterType: 'workspace',
				workspaceName: workspaceName,
				startDate,
				endDate,
				env,
				sector: sectorName,
				location,
				selectedDateInString
			}
		});
	}, [navigate, peakHourSeriesData, startDate, endDate, env, sectorName, location, selectedDateInString]);

	const handleFailureExportRequest = async () => {
		const { sortBy, sortDir } = sortingInfo ?? { sortBy: '', sortDir: '' };
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		await expensiveFailureData({
			startDate: startFormatted,
			endDate: endFormatted,
			env,
			sector,
			location,
			selectedDateInString,
			page,
			limit: pageSize,
			sortField: sortBy,
			sortOrder: sortDir,
			exportType: 'excel'
		});
	};

	const handleSuccessExportRequest = async () => {
		const { sortBy, sortDir } = sortingInfo ?? { sortBy: '', sortDir: '' };
		const startFormatted = startDate.split(' ')[0];
		const endFormatted = endDate.split(' ')[0];

		await expensiveSuccessData({
			startDate: startFormatted,
			endDate: endFormatted,
			env,
			sector,
			location,
			selectedDateInString,
			page: successPage,
			limit: successPageSize,
			sortField: sortBy,
			sortOrder: sortDir,
			exportType: 'excel'
		});
	};

	return (
		<PageWrapper>
			<FiltersBar
				location={location}
				setLocation={setLocation}
				env={env}
				setEnv={setEnv}
				envOptions={envOptions}
				setEnvOptions={setEnvOptions}
				startDate={startDate}
				endDate={endDate}
				setStartDate={setStartDate}
				setEndDate={setEndDate}
				handleDateSelect={handleDateSelect}
				selectedDateInString={selectedDateInString}
				setSelectedDateInString={setSelectedDateInString}
				sectorName={sectorName}
				setSectorName={(v) => {
					const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
					setSectorName(resolved);
					setSector(resolved);
				}}
				updateHistoryState={updateHistoryState}
				hasYTD={true}
				sectorOptionsList={sectorList}
				useHeaderFilters
			/>

			{activeTab === 0 && (
				<Grid container direction="column" spacing={1} p={{ xs: 1, md: 2 }} sx={{ overflow: 'hidden' }} >
					<Grid item>
						<Grid container spacing={1} alignItems="flex-start">
							<Grid item xs={12} md={4}>
								<Box sx={{ height: 193, mb: 1 }} id="overall-success-rate">
									<NumberWidget
										title={<HighlightedText text="Overall Success Rate" />}
										number={successRate}
										isLoading={loader.statusOverviewType}
										tooltipLabel=""
										bgColor={theme.palette.widget.background.card}
										color={theme.palette.widget.text.title}
										valueFontWeight={400}
										sameDecimalSize={true}
									/>
								</Box>
								<Box
									onClick={() => {
										const snap = {
											startDate,
											endDate,
											selectedDateInString,
										};
										sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
										navigate('/databricksADB/failed-jobs-cost', {
											state: {
												startDate,
												endDate,
												env,
												sector: sectorName,
												location,
												selectedDateInString
											}
										})
									}}
									sx={{ cursor: 'pointer', height: 193 }}
									id="total-cost-of-failed-jobs">
									<NumberWidget
										title={<HighlightedText text="Total Cost of Failed Jobs" />}
										number={
											jobfailureCost[0]?.totalCost ? `$${compactFormatter.format(jobfailureCost[0].totalCost)}` : '$0'
										}
										isLoading={loader.failureCostType}
										tooltipLabel=""
										bgColor={theme.palette.widget.background.card}
										color={theme.palette.widget.text.title}
										valueFontWeight={400}
										sameDecimalSize={true}
									/>
								</Box>
							</Grid>
							<Grid item xs={12} md={4} id="job-status-overview">
								<PieChart
									isLoading={loader.statusOverviewType}
									title="Job Status Overview"
									seriesData={(pieChartData.map((item) => ({
										name: item.name,
										value: item.value,
										percentage: '',
										formattedName: toTitleCase(item.name),
										color: item.itemStyle?.color
									})) as any)}
									dataLabel={true}
									legendLabel={true}
									chartSize="340px"
									handleEvent={(event: any) => handleStatusOverviewClick(event)}
								/>
							</Grid>
							<Grid item xs={12} md={4} id="top-10-failure-reasons">
								<PieChart
									isLoading={loader.failureReasonType}
									title="Top 10 Failure Reasons"
									seriesData={(failureReasonChartData.map((item, index) => ({
										name: item.name,
										value: item.value,
										percentage: '',
										formattedName: toTitleCase(item.name),
										color: FAILURE_REASON_COLORS[index % FAILURE_REASON_COLORS.length]
									})) as any)}
									dataLabel={true}
									legendLabel={true}
									chartSize="340px"
									handleEvent={(event: any) => handleFailureReasonClick(event)}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} container spacing={3}>
						<Grid
							xs={12}
							container
							spacing={3}
							style={{
								marginLeft: 0,
								marginTop: '10px',
								display: 'block',
								backgroundColor: 'transparent',
								padding: '0 10px'
							}}
							id="top-100-most-expensive-jobs-dbu-analysis"
						>
							<CustomizedDataGrid
								title={<HighlightedText text="Top 100 Most Expensive Failed Jobs - DBU Analysis" />}
								tableColumns={columns}
								tableData={expensiveFailure}
								tableType="incident-details-ver"
								defaultColumnNames={defaultColumns}
								page={page}
								pageSize={pageSize}
								pageSizeOptions={pageSizeOptions}
								paginationData={paginationData}
								listLength={expensiveFailure.length}
								handlepageSizeChange={handlePageSizeChange}
								handlePageChange={handlePageChange}
								isDateRangeFilter={false}
								isDisabled={false}
								isFetching={loader.expensiveFailureType}
								onSorting={handleSorting}
								isSortIcon={true}
								currSorting={sortingInfo}
								showExport={true}
								exportTableData={handleFailureExportRequest}
							/>
						</Grid>
					</Grid>
					<Grid item xs={12} container spacing={3}>
						<Grid
							xs={12}
							container
							spacing={3}
							style={{
								marginLeft: 0,
								marginTop: '10px',
								display: 'block',
								backgroundColor: 'transparent',
								padding: '0 10px'
							}}
							id="top-100-most-expensive-jobs-dbu-analysis"
						>
							<CustomizedDataGrid
								title={<HighlightedText text="Top 100 Most Expensive Succeeded Jobs - DBU Analysis" />}
								tableColumns={columnsSucce}
								tableData={expensiveSuccess}
								tableType="incident-details-ver"
								defaultColumnNames={defaultSucceColumns}
								page={successPage}
								pageSize={successPageSize}
								pageSizeOptions={pageSizeOptions}
								paginationData={successPaginationData}
								listLength={expensiveSuccess.length}
								handlepageSizeChange={handleSuccessPageSizeChange}
								handlePageChange={handleSuccessPageChange}
								isDateRangeFilter={false}
								isDisabled={false}
								isFetching={loader.expensiveSuccessType}
								onSorting={handleSorting}
								isSortIcon={true}
								currSorting={sortingInfo}
								showExport={true}
								exportTableData={handleSuccessExportRequest}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}
			{activeTab === 1 && (
				<Grid container direction="column" spacing={1} p={{ xs: 1, md: 2 }}>
					<Grid item>
						<Grid container spacing={1} alignItems="flex-start">
							<Grid item xs={12} md={6}>
								<Box sx={{ height: { xs: 'auto', md: 193 }, cursor: 'pointer' }} onClick={handlePeakHourClick}>
									<NumberWidget
										title="Overall Peak Hours"
										number={jobPeakHour[0]?.peak_hour === undefined ? '--' : `${jobPeakHour[0].peak_hour}:00`}
										isLoading={loader.PeakHourType}
										tooltipLabel=""
										bgColor={theme.palette.widget.background.card}
										color={theme.palette.widget.text.title}
										valueFontWeight={400}
										sameDecimalSize={true}
									/>
								</Box>
							</Grid>
							<Grid item xs={12} md={6}>
								<Box sx={{ height: { xs: 'auto', md: 193 }, cursor: 'pointer' }} onClick={handleActiveWorkspaceClick}>
									<NumberWidget
										title="Most Active Workspace"
										number={compactFormatter.format(activeWorkspace[0]?.total_job_runs ?? 0)}
										isLoading={loader.activeWorkspaceType}
										tooltipLabel={`${activeWorkspace[0]?.total_job_runs ?? 0}`}
										subText={activeWorkspace[0]?.active_workspace_name ?? '--'}
										units=""
										bgColor={theme.palette.widget.background.card}
										color={theme.palette.widget.text.title}
										valueFontWeight={400}
										sameDecimalSize={true}
									/>
								</Box>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Grid container spacing={1} alignItems="stretch">
							<Grid item xs={12} md={6} sx={{ display: 'flex' }}>
								<MultiLineChartWidget
									title="Common Peak Hours Overall"
									isLoading={loader.hourByJobCountType}
									data={commonPeakHoursChartData}
									xKey="name"
									xAxisLabel="Hour of Day"
									series={commonPeakHoursSeries}
									showGridLines={true}
									hasTooltip={false}
									showLegend={false}
									yMin={0}
									yInterval={200000}
									yAxisFormatter={(value: number) => compactFormatter.format(value)}
									height={340}
									tooltipFormatter={(params: any[]) => {
										if (!params?.length) return '';
										return params
											.map((item: any) => `${item.marker} ${item.name}: ${compactFormatter.format(item.value)} runs`)
											.join('<br/>');
									}}
									onLineClick={handleCommonPeakHoursClick}
									containerProps={{ sx: { height: '100%' } }}
								/>
							</Grid>
							<Grid item xs={12} md={6} sx={{ display: 'flex' }}>
								<MultiLineChartWidget
									title="Top 5 Overall Peak Hours by Workspace"
									isLoading={loader.topWorkspacesType}
									data={overallPeakHoursChartData}
									xKey="name"
									xAxisLabel=""
									series={overallPeakHoursSeries}
									showGridLines={true}
									hasTooltip={false}
									legendPosition="bottom"
									yMin={0}
									yInterval={200000}
									yAxisFormatter={(value: number) => compactFormatter.format(value)}
									height={340}
									tooltipFormatter={(params: any[]) => {
										if (!params?.length) return '';
										return params
											.map((item: any) => `${item.marker} ${item.seriesName}: ${compactFormatter.format(item.value)} runs`)
											.join('<br/>');
									}}
									onLineClick={handleOverallPeakHoursClick}
									containerProps={{ sx: { height: '100%' } }}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			)}
		</PageWrapper>
	);
};

export default JobsADB;
